/**
 * Script Injection Examples
 *
 * These examples use Tagada.* helpers and data which work across all checkout designs.
 * They DO NOT manipulate specific DOM elements (which vary between checkout implementations).
 *
 * Rule of thumb:
 *   - Tagada.ready(cb)   → DOM is ready, funnel data may still be null.
 *   - Tagada.loaded(cb)  → DOM ready AND funnel initialized; safe to read
 *                          Tagada.funnel / Tagada.checkout / Tagada.order.
 *   - Tagada.on*Update(cb) → live tracking; fires on every change.
 */

export interface ExampleItem {
  title: string;
  code: string;
}

// Basic example showing Tagada helpers
export const basicExample: ExampleItem = {
  title: '🚀 Basic',
  code: `Tagada.loaded(function() {
  // Safe to read data here — funnel is initialized.
  console.log('Page type:', Tagada.pageType);

  // Checkout page: full session + live order summary
  if (Tagada.checkout && Tagada.checkout.session) {
    console.log('Checkout ID:', Tagada.checkout.session.id);
    console.log('Customer email:', Tagada.checkout.session.customer && Tagada.checkout.session.customer.email);
    if (Tagada.checkout.orderSummary) {
      console.log('Total:', Tagada.checkout.orderSummary.totalAdjustedAmount, Tagada.checkout.orderSummary.currency);
      console.log('Items:', Tagada.checkout.orderSummary.items.length);
    }
  }

  // Thank-you page: full order
  if (Tagada.order) {
    console.log('Order ID:', Tagada.order.id);
    console.log('Paid:', Tagada.order.paidAmount, Tagada.order.currency);
  }

  // Funnel context (always present)
  if (Tagada.funnel) {
    console.log('Funnel:', Tagada.funnel.funnelId, 'Step:', Tagada.funnel.currentStepId);
  }
});`,
};

// Reactive listeners — live tracking during checkout
export const reactiveExample: ExampleItem = {
  title: '🔄 Reactive (live updates)',
  code: `// Subscribe to live updates during checkout. Each listener returns an
// unsubscribe fn. Fires whenever the underlying data changes.

var unsubSession = Tagada.onCheckoutSessionUpdate(function(d) {
  console.log('session updated:', d.checkoutSession);
  // e.g. track when customer email is first set
  if (d.checkoutSession.customer && d.checkoutSession.customer.email) {
    console.log('Customer email entered:', d.checkoutSession.customer.email);
  }
});

var unsubSummary = Tagada.onOrderSummaryUpdate(function(d) {
  console.log('summary updated:', d.orderSummary);
  // e.g. push total to analytics every time it changes
  if (typeof gtag !== 'undefined') {
    gtag('event', 'order_total_changed', {
      value: d.orderSummary.totalAdjustedAmount / 100,
      currency: d.orderSummary.currency,
    });
  }
});

var unsubOrder = Tagada.onOrderUpdate(function(d) {
  console.log('order updated (thank-you):', d.order);
});

// Cleanup on unload
window.addEventListener('beforeunload', function() {
  unsubSession();
  unsubSummary();
  unsubOrder();
});`,
};

// GA4 purchase tracking
export const ga4Example: ExampleItem = {
  title: '📊 GA4',
  code: `<script async src="https://www.googletagmanager.com/gtag/js?id=G-XXXXXXXXXX"></script>
<script>
window.dataLayer = window.dataLayer || [];
function gtag(){ dataLayer.push(arguments); }
gtag('js', new Date());
gtag('config', 'G-XXXXXXXXXX');

Tagada.loaded(function() {
  if (Tagada.order) {
    gtag('event', 'purchase', {
      transaction_id: Tagada.order.id,
      value: Tagada.order.paidAmount / 100,
      currency: Tagada.order.currency,
    });
  }
});
</script>`,
};

// Google Ads conversion
export const googleAdsExample: ExampleItem = {
  title: '🎯 Google Ads',
  code: `<script async src="https://www.googletagmanager.com/gtag/js?id=AW-XXXXXXXXXX"></script>
<script>
window.dataLayer = window.dataLayer || [];
function gtag(){ dataLayer.push(arguments); }
gtag('js', new Date());
gtag('config', 'AW-XXXXXXXXXX');

Tagada.loaded(function() {
  if (Tagada.order) {
    gtag('event', 'conversion', {
      send_to: 'AW-XXXXXXXXXX/CONVERSION_LABEL',
      transaction_id: Tagada.order.id,
      value: Tagada.order.paidAmount / 100,
      currency: Tagada.order.currency,
    });
  }
});
</script>`,
};

// Facebook Pixel
export const fbPixelExample: ExampleItem = {
  title: '📱 FB Pixel',
  code: `<script>
!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?
n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;
n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;
t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,
document,'script','https://connect.facebook.net/en_US/fbevents.js');

fbq('init', 'YOUR_PIXEL_ID');
fbq('track', 'PageView');

Tagada.loaded(function() {
  if (Tagada.order) {
    fbq('track', 'Purchase', {
      value: Tagada.order.paidAmount / 100,
      currency: Tagada.order.currency,
      content_type: 'product',
    });
  }
});
</script>`,
};

// Custom tracking / webhook example
export const trackingExample: ExampleItem = {
  title: '📡 Tracking',
  code: `Tagada.loaded(function() {
  console.log('[Tracking] Page:', Tagada.pageType);

  // On checkout page - track begin_checkout once we have the session
  if (Tagada.checkout && Tagada.checkout.session) {
    var data = {
      event: 'begin_checkout',
      checkout_id: Tagada.checkout.session.id,
      value: Tagada.checkout.orderSummary && Tagada.checkout.orderSummary.totalAdjustedAmount / 100,
      currency: Tagada.checkout.orderSummary && Tagada.checkout.orderSummary.currency,
    };
    console.log('[Tracking] Begin Checkout:', data);
    // fetch('https://your-api.com/track', { method: 'POST', body: JSON.stringify(data) });
  }

  // On thank-you page - track purchase
  if (Tagada.order) {
    var data = {
      event: 'purchase',
      order_id: Tagada.order.id,
      value: Tagada.order.paidAmount / 100,
      currency: Tagada.order.currency,
      customer_email: Tagada.order.customer && Tagada.order.customer.email,
    };
    console.log('[Tracking] Purchase:', data);
    // fetch('https://your-api.com/track', { method: 'POST', body: JSON.stringify(data) });
  }
});

// Optional: track total changes during checkout for abandonment analytics
Tagada.onOrderSummaryUpdate(function(d) {
  console.log('[Tracking] Total changed:', d.orderSummary.totalAdjustedAmount);
});`,
};

import { adTrackerPostbackExample } from './adTrackerPostback';
export { adTrackerPostbackExample };

export const allExamples = {
  basic: basicExample,
  reactive: reactiveExample,
  tracking: trackingExample,
  adTrackerPostback: adTrackerPostbackExample,
  ga4: ga4Example,
  googleAds: googleAdsExample,
  fbPixel: fbPixelExample,
};
